import is from '@sindresorhus/is';
import { type FunctionComponent, useCallback, useEffect, useRef } from 'react';

import { MAIN_TILE_ID, TILE_WIDTH_CSS_TRANSITION } from './constants';
import {
    useSetTileBox,
    useTileErrorBoundary,
    useTileWidthContext,
    useTilingEngineContext,
} from './TilingEngineContext';
import type { TilingEngineMainProps } from './types';

export const TilingEngineMain: FunctionComponent<TilingEngineMainProps> = ({ children, minWidth }) => {
    const { notify } = useTilingEngineContext();
    const setTileBox = useSetTileBox();
    const tileWidths = useTileWidthContext();
    const ErrorBoundary = useTileErrorBoundary();

    const widthStyle = tileWidths.get(MAIN_TILE_ID);
    const width = widthStyle?.width ?? '100%';

    const ref = useRef<HTMLDivElement>(null);
    const previousWidthRef = useRef<string | null>(null);

    const setOuterRef = useCallback(
        (node: HTMLDivElement | null) => {
            ref.current = node;
            setTileBox(MAIN_TILE_ID, node);
        },
        [setTileBox],
    );

    useEffect(() => {
        if (previousWidthRef.current === null) {
            previousWidthRef.current = width;
            return;
        }

        if (previousWidthRef.current === width) {
            return;
        }

        previousWidthRef.current = width;
        notify({ type: 'tileOpenStart', tileId: MAIN_TILE_ID });

        const node = ref.current;

        if (is.nullOrUndefined(node)) {
            notify({ type: 'tileOpenComplete', tileId: MAIN_TILE_ID });
            return;
        }

        const handleTransitionEnd = (event: TransitionEvent): void => {
            if (event.propertyName !== 'width' || event.target !== node) {
                return;
            }

            node.removeEventListener('transitionend', handleTransitionEnd);
            notify({ type: 'tileOpenComplete', tileId: MAIN_TILE_ID });
        };

        node.addEventListener('transitionend', handleTransitionEnd);

        return () => {
            node.removeEventListener('transitionend', handleTransitionEnd);
        };
    }, [width, notify]);

    return (
        <div
            ref={setOuterRef}
            style={{
                flexShrink: 0,
                height: '100%',
                maxWidth: widthStyle?.maxWidth,
                minWidth: widthStyle?.minWidth ?? minWidth,
                transition: TILE_WIDTH_CSS_TRANSITION,
                width,
            }}
        >
            <div className="h-full w-full flex flex-col">
                {is.nullOrUndefined(ErrorBoundary) ? (
                    children
                ) : (
                    <ErrorBoundary tileId={MAIN_TILE_ID}>{children}</ErrorBoundary>
                )}
            </div>
        </div>
    );
};
